import { NotificationsActionTypes } from "./constants";

const INIT_STATE = {
  list: [],
  total: 0,
  unreadCount: 0,
  loading: false,
  error: "",
};

interface NotificationActionType {
  type: NotificationsActionTypes;
  payload: {
    actionType?: string;
    data?: any;
    notification?: any;
    count?: number;
    error?: string;
    id?: string;
    skip?: number;
  };
}

interface State {
  list: any[];
  total: number;
  unreadCount: number;
  loading: boolean;
  error: string;
}

const Notifications = (
  state: State = INIT_STATE,
  action: NotificationActionType
): State => {
  switch (action.type) {
    // Get Notifications
    case NotificationsActionTypes.GET_NOTIFICATIONS:
      return {
        ...state,
        loading: true,
        error: "",
      };

    case NotificationsActionTypes.GET_NOTIFICATIONS_SUCCESS: {
      const items = action.payload.data?.items || [];
      const skip = Number(action.payload.skip || 0);
      const list =
        skip > 0
          ? [
              ...state.list,
              ...items.filter(
                (n: any) => !state.list.some((x) => x._id === n._id)
              ),
            ]
          : items;
      return {
        ...state,
        list,
        total: action.payload.data?.total || 0,
        loading: false,
        error: "",
      };
    }

    case NotificationsActionTypes.GET_NOTIFICATIONS_ERROR:
      return {
        ...state,
        loading: false,
        error: action.payload.error || "Error fetching notifications",
      };

    // Get Unread Count
    case NotificationsActionTypes.GET_UNREAD_COUNT:
      return {
        ...state,
        // Don't set loading for unread count to avoid UI flicker
      };

    case NotificationsActionTypes.GET_UNREAD_COUNT_SUCCESS:
      return {
        ...state,
        unreadCount: action.payload.count || 0,
        loading: false,
      };

    case NotificationsActionTypes.GET_UNREAD_COUNT_ERROR:
      return {
        ...state,
        loading: false,
        error: action.payload.error || "Error fetching unread count",
      };

    // Mark as Read
    case NotificationsActionTypes.MARK_AS_READ:
      return {
        ...state,
      };

    case NotificationsActionTypes.MARK_AS_READ_SUCCESS:
      // Find the notification being marked as read
      const notificationToMark = state.list.find(
        (n) => n._id === action.payload.data?.id || n._id === action.payload.id
      );

      // Only decrease count if notification was previously unread
      const shouldDecreaseCount =
        notificationToMark && !notificationToMark.isRead;

      return {
        ...state,
        list: state.list.map((notification) =>
          notification._id === (action.payload.data?.id || action.payload.id)
            ? { ...notification, isRead: true }
            : notification
        ),
        unreadCount: shouldDecreaseCount
          ? Math.max(0, state.unreadCount - 1)
          : state.unreadCount,
        loading: false,
      };

    case NotificationsActionTypes.MARK_AS_READ_ERROR:
      return {
        ...state,
        loading: false,
        error: action.payload.error || "Error marking as read",
      };

    // Mark All as Read
    case NotificationsActionTypes.MARK_ALL_AS_READ:
      return {
        ...state,
      };

    case NotificationsActionTypes.MARK_ALL_AS_READ_SUCCESS:
      return {
        ...state,
        list: state.list.map((notification) => ({
          ...notification,
          isRead: true,
        })),
        unreadCount: 0,
        loading: false,
      };

    case NotificationsActionTypes.MARK_ALL_AS_READ_ERROR:
      return {
        ...state,
        loading: false,
        error: action.payload.error || "Error marking all as read",
      };

    // Add Notification (Real-time from Socket.IO)
    case NotificationsActionTypes.ADD_NOTIFICATION:
      // Don't add duplicate notifications
      const isDuplicate = state.list.some(
        (n) => n._id === action.payload.notification._id
      );

      if (isDuplicate) {
        return state;
      }

      return {
        ...state,
        list: [action.payload.notification, ...state.list],
        unreadCount: state.unreadCount + 1,
        total: state.total + 1,
      };

    // Remove Notification
    case NotificationsActionTypes.REMOVE_NOTIFICATION:
      const removedNotification = state.list.find(
        (n) => n._id === action.payload.id
      );
      return {
        ...state,
        list: state.list.filter((n) => n._id !== action.payload.id),
        unreadCount:
          removedNotification && !removedNotification.isRead
            ? Math.max(0, state.unreadCount - 1)
            : state.unreadCount,
        total: Math.max(0, state.total - 1),
      };

    // Clear All Notifications
    case NotificationsActionTypes.CLEAR_NOTIFICATIONS:
      return {
        ...INIT_STATE,
      };

    default:
      return state;
  }
};

export default Notifications;
